import { Theme, makeStyles, createStyles } from '@material-ui/core';
import React, { useEffect, useState } from 'react';
import { MatterSearchField } from '../MatterSearchField';
import { MatterTerminology } from '../../MatterTerminology';
import { useTenant } from '../../hooks/useTenant';
import { SkeletonLoader } from '../../components/loading/SkeletonLoader';
import { AnimatedItem } from '../../components/animations/AnimatedItem';
import { DocumentCard } from './DocumentCard';
import { MATTER_DETAILS_REFRESH_EVENT } from '../MatterDetails';
import EventSystem from '../../EventSystem';
import { DocumentQuery, MatterDocument, MatterDocumentData, MatterDocumentParams } from './hooks/useMatterDocumentQuery';
import { useDeleteDocumentMutation } from './hooks/useDeleteDocumentMutation';
import { showNotification } from '../../App';
import { useDeleteDocumentNotificationSubscription } from '../../hooks/useDeleteDocumentNotificationSubscription';
import Pagination from '@material-ui/lab/Pagination';
import { useLazyQuery } from 'react-apollo';

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        root: {
            padding: '25px 18px',
            display: 'inline-block',
            width: '100%',
        },
        searchRow: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: theme.spacing(2),
            paddingBottom: theme.spacing(2.5),
            marginBottom: theme.spacing(2.5),
            borderBottom: `1px solid ${theme.palette.divider}`,
        },
        searchField: {
            flex: 1,
            maxWidth: 400,
        },
        paginationWrapper: {
            padding: '20px 0',
            display: 'flex',
            justifyContent: 'flex-end',
        },
        documentGrid: {
            display: 'flex',
            flexDirection: 'column',
            gap: theme.spacing(1.25),
        },
        noResults: {
            textAlign: 'center',
            padding: '40px 0',
            color: theme.palette.text.secondary,
        },
    })
);

interface DocumentTabProps {
    matterId: string;
}

export const DocumentTab: React.FC<DocumentTabProps> = (props) => {

    const classes = useStyles();

    const tenant = useTenant();

    const [search, setSearch] = useState<string>('');

    const [page, setPage] = useState<number>(1);

    const [getDocumentQuery, documentQuery] = useLazyQuery<MatterDocumentData, MatterDocumentParams>(
        DocumentQuery,
        // Was inheriting the app's global default (cache-and-network) - always re-fetching on
        // every tab mount regardless of cache. Cached after the first load; the header's
        // Refresh button (MatterDetails.tsx) forces a re-fetch via documentQuery.refetch()
        // below (re-invoking getDocumentQuery would just hit the now-cached result instead).
        { fetchPolicy: 'cache-first' }
    );

    useEffect(() => {
        getDocumentQuery({
            variables: {
                filter: search,
                id: props.matterId,
                first: 10,
                offset: 0
            }
        });
    // tslint:disable-next-line: align
    }, []);

    const documentQueryRefetch = documentQuery.refetch;
    useEffect(() => {
        const onRefresh = () => {
            documentQueryRefetch?.();
        };
        EventSystem.on(MATTER_DETAILS_REFRESH_EVENT, onRefresh);
        return () => { EventSystem.event.removeListener(MATTER_DETAILS_REFRESH_EVENT, onRefresh); };
    // tslint:disable-next-line: align
    }, [documentQueryRefetch]);

    const deleteDocument = useDeleteDocumentMutation();

    const deleteNotification = useDeleteDocumentNotificationSubscription('no-cache');
    
    useEffect(() => {
        if (!deleteNotification.loading && !deleteNotification.error && deleteNotification.data) {
            
            const { deleteDocumentNotification } = deleteNotification.data;

            if (deleteDocumentNotification.status) {

                showNotification('Success', deleteDocumentNotification.message, 'info');

                getDocumentQuery({
                    variables: {
                        filter: search,
                        id: props.matterId,
                        first: 10,
                        offset: 0
                    }
                });
                setPage(1);

            } else {
                showNotification('Failed', deleteDocumentNotification.message, 'error');
            }
        }
        
        if (deleteNotification.error) {
            showNotification('Failed', deleteNotification.error.message, 'error');
        }

    // eslint-disable-next-line react-hooks/exhaustive-deps
    },        [deleteNotification.data, deleteNotification.error, deleteNotification.loading]);

    useEffect(() => {
        if (deleteDocument.data && !deleteDocument.loading && !deleteDocument.error) {
            showNotification(null, 'Successfully deleted', 'info');
        }
        if (deleteDocument.error) {
            showNotification(null, deleteDocument.error.message, 'error');
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    },        [deleteDocument.data, deleteDocument.loading, deleteDocument.error]);
    
    const onSearch = (filter: string) => {
        setSearch(filter);
        getDocumentQuery({
            variables: {
                filter: filter,
                id: props.matterId,
                first: 10,
                offset: 0
            }
        });
        setPage(1);
    };

    const onDelete = (documentId: number, matterId: number) => {

        deleteDocument.deleteData({
            variables: {
                documentId: documentId,
                matterId: matterId
            }
        });

    };

    const handlePaginationChange = (event: React.ChangeEvent<unknown>, value: number) => {
        setPage(value);
        getDocumentQuery({
            variables: {
                filter: search,
                id: props.matterId,
                first: 10,
                offset: (value - 1) * 10
            }
        });
    };

    const getDocumentCards = () => {
        if (tenant.loading || documentQuery.loading) {
            return <SkeletonLoader variant="panel" columns={1} rows={5} />;
        }

        const documents = documentQuery.data?.matter.documents.documents;

        if (!documents || documents.length === 0) {
            return <div className={classes.noResults}>{MatterTerminology.NoDocumentsAvailable}</div>;
        }

        return (
            <div className={classes.documentGrid}>
                {documents.map((doc: MatterDocument, idx: number) => (
                    <AnimatedItem key={idx} noFill={true} delay={idx * 30}>
                        <DocumentCard
                            data={doc}
                            sourceSystem={tenant.data!.tenant.sourceSystem}
                            onDelete={onDelete}
                        />
                    </AnimatedItem>
                ))}
            </div>
        );
    };

    const pageCount = Math.round((documentQuery.data?.matter.documents.recordCount || 0) / 10);
    
    return (
        <div className={classes.root}>
            <div className={classes.searchRow}>
                <div className={classes.searchField}>
                    <MatterSearchField
                        hint={MatterTerminology.SearchDocument}
                        search={search}
                        searchAction={onSearch}
                        accent="#616161"
                    />
                </div>
            </div>
            {pageCount > 0 && (
                <div
                    className={classes.paginationWrapper}
                >
                    <Pagination 
                        page={page}
                        count={pageCount} 
                        color="primary" 
                        onChange={handlePaginationChange} 
                    />
                </div>
            )}
            {getDocumentCards()}
            {pageCount > 0 && (
                <div
                    className={classes.paginationWrapper}
                >
                    <Pagination 
                        page={page}
                        count={pageCount} 
                        color="primary" 
                        onChange={handlePaginationChange} 
                    />
                </div>
            )}
        </div>
    );
};